import { useCallback, useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { api } from '../../../shared/services/API'
import { useNotification } from '../../../shared/contexts/NotificationContext'
import { TIPO_ADDON_LABEL, formatMoney, type Addon } from '../subscricoesModel'
import AddonFormDialog from '../components/AddonFormDialog'

export default function AddonsList() {
    const { notify } = useNotification()

    const [rows, setRows] = useState<Addon[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState('')

    const [formOpen, setFormOpen] = useState(false)
    const [editing, setEditing] = useState<Addon | null>(null)

    const fetch = useCallback(() => {
        setIsLoading(true)
        setError('')
        api.get<Addon[]>('/api/admin/addons')
            .then((r) => setRows(r.data ?? []))
            .catch(() => setError('Não foi possível carregar os add-ons.'))
            .finally(() => setIsLoading(false))
    }, [])

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- fetch alterna a sua própria flag de loading
        fetch()
    }, [fetch])

    return (
        <Box>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>Add-ons</Typography>
                <Typography variant="body2" color="text.secondary">
                    Serviços adicionais que os tenants podem contratar além do plano.
                </Typography>
            </Box>

            {error && <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>{error}</Alert>}

            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Nome</TableCell>
                                <TableCell>Tipo</TableCell>
                                <TableCell align="right">Preço</TableCell>
                                <TableCell>Estado</TableCell>
                                <TableCell align="right">Acções</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 6 }}><CircularProgress size={24} /></TableCell></TableRow>
                            ) : rows.length === 0 ? (
                                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 6, color: 'text.secondary' }}>Ainda não há add-ons.</TableCell></TableRow>
                            ) : (
                                rows.map((a) => (
                                    <TableRow key={a.id} hover>
                                        <TableCell sx={{ fontWeight: 600 }}>{a.nome}</TableCell>
                                        <TableCell>{TIPO_ADDON_LABEL[a.tipo]}</TableCell>
                                        <TableCell align="right">{formatMoney(a.preco)}</TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={a.activo ? 'Activo' : 'Inactivo'}
                                                sx={{ fontWeight: 600, bgcolor: a.activo ? '#F0FDF4' : '#F3F4F6', color: a.activo ? '#15803D' : '#6B7280' }}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Editar">
                                                <IconButton size="small" onClick={() => { setEditing(a); setFormOpen(true) }} aria-label="Editar">
                                                    <EditOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <AddonFormDialog
                open={formOpen}
                initial={editing}
                onClose={() => setFormOpen(false)}
                onSaved={() => { setFormOpen(false); notify('Add-on actualizado.'); fetch() }}
            />
        </Box>
    )
}
